import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type { Permission } from '@platform/contracts';

import { api } from './api';
import { canAccess } from './modules';
import { useMe } from './queries';
import { useScope } from './use-scope';

/**
 * Query de la consola atada al alcance actual (Todas / cliente / empresa): la clave incluye el
 * alcance, así cambiar el selector refresca todas las pantallas sin lógica extra.
 */
export function useScopedQuery<T>(
  key: readonly unknown[],
  path: string,
  /** `keepPrevious`: al cambiar filtros o página se sigue viendo lo anterior hasta que llega lo nuevo. */
  opts: { enabled?: boolean; requires?: Permission; keepPrevious?: boolean } = {},
) {
  const { scope } = useScope();
  const me = useMe();
  // Sin el permiso la página muestra "Sin acceso" y ni siquiera se pide nada a la API.
  const allowed = canAccess(me, opts.requires ?? 'data:read');
  return useQuery({
    queryKey: ['console', scope, ...key],
    queryFn: ({ signal }) => api<T>(path, { scope, signal }),
    enabled: allowed && (opts.enabled ?? true),
    refetchInterval: 60_000,
    ...(opts.keepPrevious ? { placeholderData: keepPreviousData } : {}),
  });
}

/** slug → nombre de la empresa (lo que ve el usuario); `null` = tarea de la plataforma. */
export function useTenantName(): (slug: string | null | undefined) => string {
  const me = useMe();
  const names = new Map(me.clients.flatMap((c) => c.tenants.map((t) => [t.slug, t.name] as const)));
  return (slug) => (slug ? (names.get(slug) ?? slug) : 'Plataforma');
}

/** slug → logo de la empresa (subido o favicon de su web); `null` = monograma. */
export function useTenantLogo(): (slug: string | null | undefined) => string | null {
  const me = useMe();
  const logos = new Map(
    me.clients.flatMap((c) => c.tenants.map((t) => [t.slug, t.logoUrl ?? null] as const)),
  );
  return (slug) => (slug ? (logos.get(slug) ?? null) : null);
}

// Siempre con separador de miles ("7.500", no "7500"): la consola mezcla cifras de 4 y 5 dígitos.
const nf = new Intl.NumberFormat('es-ES', { useGrouping: 'always' });
const pf = new Intl.NumberFormat('es-ES', { style: 'percent', maximumFractionDigits: 1 });
// Mismo estilo de fecha que el resto de la consola ("23 sept 2026, 18:23"), no el numérico "23/9/26".
const df = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeStyle: 'short' });

export const fmtNum = (n: number): string => nf.format(n);
export const fmtPct = (n: number | null): string => (n === null ? '—' : pf.format(n));
export const fmtDateTime = (iso: string | null): string => (iso ? df.format(new Date(iso)) : '—');

/** `2026-09-21` → `21/9`. */
export function fmtDay(date: string): string {
  const [, m, d] = date.split('-');
  return `${String(Number(d))}/${String(Number(m))}`;
}

export function fmtDuration(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${String(ms)} ms`;
  const s = Math.round(ms / 1000);
  return s < 120 ? `${String(s)} s` : `${String(Math.round(s / 60))} min`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${String(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Nombre legible de los eventos de Instantly. */
export const EVENT_LABELS: Record<string, string> = {
  email_sent: 'Enviado',
  email_opened: 'Abierto',
  email_link_clicked: 'Click',
  reply_received: 'Respuesta',
  email_bounced: 'Rebote',
  lead_unsubscribed: 'Baja',
  lead_interested: 'Interesado',
  lead_not_interested: 'No interesado',
  lead_meeting_booked: 'Reunión agendada',
  lead_meeting_completed: 'Reunión hecha',
  lead_closed: 'Cerrado',
  lead_out_of_office: 'Fuera de oficina',
  lead_wrong_person: 'Persona equivocada',
  account_error: 'Error de cuenta',
  campaign_completed: 'Campaña completada',
};
